/**
 * Mix & Match Page — Product Picker & Cart Integration
 *
 * Features:
 * - Exactly 2 product selection with numbered badges
 * - Live discount computation
 * - Desktop sidebar + mobile bottom bar
 * - Proper cart drawer opening via the theme's native 'cart:update' event
 * - Section re-rendering so cart drawer shows updated contents
 */
(function () {
  'use strict';

  document.querySelectorAll('[id^="MixMatchPage-"]').forEach(
    /** @param {HTMLElement} sectionEl */
    function (sectionEl) {
      initMixMatchPage(sectionEl);
    }
  );

  /** @param {HTMLElement} sectionEl */
  function initMixMatchPage(sectionEl) {
    var sectionId = sectionEl.dataset.sectionId || '';
    var currency = sectionEl.dataset.currency || 'EGP';

    // DOM refs - Cards
    /** @type {HTMLElement[]} */
    var cards = /** @type {HTMLElement[]} */ (Array.from(sectionEl.querySelectorAll('.mmp__card')));

    // DOM refs - Sidebar
    var sidebarEmpty = document.getElementById('mmpEmpty-' + sectionId);
    var sidebarFilled = document.getElementById('mmpFilled-' + sectionId);
    var item1El = document.getElementById('mmpItem1-' + sectionId);
    var item2El = document.getElementById('mmpItem2-' + sectionId);
    var originalEl = document.getElementById('mmpOriginal-' + sectionId);
    var bundleEl = document.getElementById('mmpBundle-' + sectionId);
    var savedEl = document.getElementById('mmpSaved-' + sectionId);
    var addBtn = /** @type {HTMLButtonElement | null} */ (document.getElementById('mmpAddBtn-' + sectionId));

    // DOM refs - Mobile bar
    var mobileBar = document.getElementById('mmpMobileBar-' + sectionId);
    var mobileThumbs = document.getElementById('mmpMobileThumbs-' + sectionId);
    var mobileLabel = document.getElementById('mmpMobileLabel-' + sectionId);
    var mobilePrice = document.getElementById('mmpMobilePrice-' + sectionId);
    var mobileBtn = /** @type {HTMLButtonElement | null} */ (document.getElementById('mmpMobileBtn-' + sectionId));

    // DOM refs - Misc
    var selectedCountEl = document.getElementById('mmpSelectedCount-' + sectionId);

    // Steps
    var steps = sectionEl.querySelectorAll('.mmp__step');
    var stepLines = sectionEl.querySelectorAll('.mmp__step-line');

    // State
    /** @type {HTMLElement[]} */
    var selected = []; // max 2 card elements
    var isAdding = false;

    // Init
    updateSteps();
    bindEvents();

    // ===== EVENT BINDING =====
    function bindEvents() {
      cards.forEach(
        /** @param {HTMLElement} card */
        function (card) {
          card.addEventListener('click', function () {
            if (!isAdding) handleCardClick(card);
          });
          card.addEventListener('keydown',
            /** @param {KeyboardEvent} e */
            function (e) {
              if ((e.key === 'Enter' || e.key === ' ') && !isAdding) {
                e.preventDefault();
                handleCardClick(card);
              }
            }
          );
        }
      );

      // Sidebar remove buttons
      sectionEl.querySelectorAll('.mmp__sidebar-item-remove').forEach(
        /** @param {HTMLElement} btn */
        function (btn) {
          btn.addEventListener('click', function () {
            var slot = parseInt(btn.dataset.slot || '0', 10);
            if (selected[slot]) {
              removeSelection(slot);
            }
          });
        }
      );

      // Add to cart buttons
      if (addBtn) addBtn.addEventListener('click', handleAddToCart);
      if (mobileBtn) mobileBtn.addEventListener('click', handleAddToCart);
    }

    // ===== CARD CLICK =====
    /** @param {HTMLElement} card */
    function handleCardClick(card) {
      var idx = selected.indexOf(card);

      if (idx !== -1) {
        // Deselect
        removeSelection(idx);
      } else {
        if (selected.length >= 2) {
          // Replace oldest
          var oldest = selected[0];
          if (oldest) deselectCard(oldest);
          selected.shift();
        }
        selected.push(card);
        // Re-number all selected badges
        renumberBadges();
      }

      updateUI();
    }

    /** Re-number all selected card badges to match their current position */
    function renumberBadges() {
      selected.forEach(
        /** @param {HTMLElement} c @param {number} i */
        function (c, i) {
          c.classList.add('is-selected');
          c.setAttribute('aria-pressed', 'true');
          var badge = c.querySelector('.mmp__card-badge-num');
          if (badge) badge.textContent = String(i + 1);
        }
      );
      // Pulse animation on the last added card
      /** @type {HTMLElement | undefined} */
      var lastCard = selected[selected.length - 1];
      if (lastCard !== undefined) {
        lastCard.classList.add('just-selected');
        var ref = lastCard;
        setTimeout(function () { ref.classList.remove('just-selected'); }, 500);
      }
    }

    /** @param {number} index */
    function removeSelection(index) {
      var card = selected[index];
      if (card) {
        deselectCard(card);
        selected.splice(index, 1);
        // Re-number remaining
        renumberBadges();
        updateUI();
      }
    }

    /**
     * @param {HTMLElement} card
     * @param {number} number
     */
    function selectCard(card, number) {
      card.classList.add('is-selected');
      card.setAttribute('aria-pressed', 'true');
      var badge = card.querySelector('.mmp__card-badge-num');
      if (badge) badge.textContent = String(number);

      // Pulse animation
      card.classList.add('just-selected');
      setTimeout(function () { card.classList.remove('just-selected'); }, 500);
    }

    /** @param {HTMLElement} card */
    function deselectCard(card) {
      card.classList.remove('is-selected');
      card.setAttribute('aria-pressed', 'false');
    }

    // ===== UI UPDATE =====
    function updateUI() {
      var count = selected.length;
      if (selectedCountEl) selectedCountEl.textContent = String(count);
      updateSteps();
      updateSidebar();
      updateMobileBar();
    }

    function updateSteps() {
      var count = selected.length;
      steps.forEach(
        /** @param {Element} step @param {number} _i */
        function (step, _i) {
          step.classList.remove('is-active', 'is-done');
        }
      );
      stepLines.forEach(
        /** @param {Element} line */
        function (line) {
          line.classList.remove('is-active');
        }
      );

      if (count === 0) {
        if (steps[0]) steps[0].classList.add('is-active');
      } else if (count === 1) {
        if (steps[0]) steps[0].classList.add('is-done');
        if (stepLines[0]) stepLines[0].classList.add('is-active');
        if (steps[1]) steps[1].classList.add('is-active');
      } else if (count >= 2) {
        if (steps[0]) steps[0].classList.add('is-done');
        if (steps[1]) steps[1].classList.add('is-done');
        if (stepLines[0]) stepLines[0].classList.add('is-active');
        if (stepLines[1]) stepLines[1].classList.add('is-active');
        if (steps[2]) steps[2].classList.add('is-active');
      }
    }

    function updateSidebar() {
      if (!sidebarEmpty || !sidebarFilled) return;

      if (selected.length === 0) {
        sidebarEmpty.style.display = '';
        sidebarFilled.style.display = 'none';
        if (addBtn) addBtn.disabled = true;
        return;
      }

      sidebarEmpty.style.display = 'none';
      sidebarFilled.style.display = '';

      // Item 1
      updateSidebarItem(item1El, selected[0] || null);
      // Item 2
      updateSidebarItem(item2El, selected[1] || null);

      // Pricing
      var pricing = computePricing();
      if (originalEl) originalEl.textContent = formatNum(pricing.realTotal) + ' ' + currency;
      if (bundleEl) bundleEl.textContent = formatNum(pricing.minTotal > 0 ? pricing.minTotal : pricing.realTotal) + ' ' + currency;
      if (savedEl) {
        if (pricing.saved > 0) {
          savedEl.textContent = formatNum(pricing.saved) + ' ' + currency + ' (' + pricing.discountPct + '%)';
        } else {
          savedEl.textContent = '—';
        }
      }

      if (addBtn) addBtn.disabled = selected.length !== 2;
    }

    /**
     * @param {HTMLElement | null} el
     * @param {HTMLElement | null} card
     */
    function updateSidebarItem(el, card) {
      if (!el) return;
      var imgContainer = el.querySelector('.mmp__sidebar-item-img');
      var nameEl = el.querySelector('.mmp__sidebar-item-name');
      var priceEl = el.querySelector('.mmp__sidebar-item-price');

      if (card) {
        if (imgContainer) {
          imgContainer.innerHTML = '<img src="' + escHtml(card.dataset.productImage || '') + '" alt="" width="52" height="52" loading="lazy">';
        }
        if (nameEl) nameEl.textContent = card.dataset.productTitle || '—';
        if (priceEl) {
          var minP = parseFloat(card.dataset.minPrice || '0') || 0;
          priceEl.textContent = minP > 0
            ? 'Bundle: ' + formatNum(minP) + ' ' + currency
            : formatNum(parseFloat(card.dataset.realPrice || '0') || 0) + ' ' + currency;
        }
        el.style.opacity = '1';
      } else {
        if (imgContainer) imgContainer.innerHTML = '';
        if (nameEl) nameEl.textContent = '—';
        if (priceEl) priceEl.textContent = '—';
        el.style.opacity = '0.35';
      }
    }

    function updateMobileBar() {
      if (!mobileBar) return;
      var count = selected.length;

      if (count > 0) {
        mobileBar.classList.add('is-visible');
      } else {
        mobileBar.classList.remove('is-visible');
      }

      // Thumbnails
      if (mobileThumbs) {
        var html = '';
        for (var i = 0; i < 2; i++) {
          var selCard = selected[i];
          if (selCard) {
            html += '<div class="mmp__mobile-bar-thumb"><img src="' + escHtml(selCard.dataset.productImage || '') + '" alt="" width="36" height="36"></div>';
          } else {
            html += '<div class="mmp__mobile-bar-thumb mmp__mobile-bar-thumb--empty"></div>';
          }
          if (i === 0) html += '<span class="mmp__mobile-bar-plus">+</span>';
        }
        mobileThumbs.innerHTML = html;
      }

      // Pricing
      var pricing = computePricing();
      if (mobileLabel) {
        if (count < 2) {
          mobileLabel.textContent = 'Select ' + (2 - count) + ' more';
        } else {
          mobileLabel.textContent = pricing.saved > 0 ? 'You save ' + formatNum(pricing.saved) + ' ' + currency : 'Bundle ready';
        }
      }
      if (mobilePrice) {
        mobilePrice.textContent = count >= 2
          ? formatNum(pricing.minTotal > 0 ? pricing.minTotal : pricing.realTotal) + ' ' + currency
          : '';
      }

      if (mobileBtn) mobileBtn.disabled = count !== 2;
    }

    // ===== PRICING =====
    function computePricing() {
      var realTotal = 0, minTotal = 0;
      selected.forEach(
        /** @param {HTMLElement} card */
        function (card) {
          realTotal += parseFloat(card.dataset.realPrice || '0') || 0;
          minTotal += parseFloat(card.dataset.minPrice || '0') || 0;
        }
      );

      realTotal = round2(realTotal);
      minTotal = round2(minTotal);

      var saved = 0, discountPct = 0;
      if (realTotal > minTotal && minTotal > 0) {
        saved = round2(realTotal - minTotal);
        discountPct = round2((saved / realTotal) * 100);
      }

      return { realTotal: realTotal, minTotal: minTotal, saved: saved, discountPct: discountPct };
    }

    // ===== ADD TO CART =====
    function handleAddToCart() {
      if (selected.length !== 2 || isAdding) return;
      isAdding = true;

      var bundleId = 'mm_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);

      var items = selected.map(
        /** @param {HTMLElement} card */
        function (card) {
          return {
            id: parseInt(card.dataset.variantId || '0', 10),
            quantity: 1,
            properties: {
              _bundle: 'mixmatch',
              _bundle_id: bundleId,
              _min_price: card.dataset.minPrice || '0',
              _product_title: card.dataset.productTitle || '',
              _product_image: card.dataset.productImage || '',
              _product_url: card.dataset.productUrl || ''
            }
          };
        }
      );

      // Get section IDs from cart-items-component for section rendering
      /** @type {string[]} */
      var sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach(
        /** @param {Element} el */
        function (el) {
          var htmlEl = /** @type {HTMLElement} */ (el);
          if (htmlEl.dataset && htmlEl.dataset.sectionId) {
            sectionIds.push(htmlEl.dataset.sectionId);
          }
        }
      );

      /** @type {{ items: typeof items, sections?: string }} */
      var payload = { items: items };
      if (sectionIds.length > 0) {
        payload.sections = sectionIds.join(',');
      }

      // UI feedback
      setButtonState('loading');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed (' + res.status + ')');
          return res.json();
        })
        .then(
          /** @param {Record<string, unknown>} response */
          function (response) {
            // Dispatch the native 'cart:update' event
            // This triggers:
            //  - cart-drawer-component → auto-open (if configured)
            //  - cart-items-component → morph section with new HTML
            //  - cart-icon → update bubble count
            var evt = new Event('cart:update', { bubbles: true });
            /** @type {Record<string, unknown>} */ (/** @type {unknown} */ (evt)).detail = {
              resource: {},
              sourceId: 'mix-match-page',
              data: {
                source: 'mix-match',
                sections: response.sections || {},
                itemCount: 2
              }
            };
            document.dispatchEvent(evt);

            // Success UI
            setButtonState('success');

            // Reset selection after short delay
            setTimeout(function () {
              selected.forEach(
                /** @param {HTMLElement} c */
                function (c) { deselectCard(c); }
              );
              selected = [];
              updateUI();
              setButtonState('idle');
              isAdding = false;
            }, 1800);
          }
        )
        .catch(function (err) {
          console.error('Mix & Match add to cart error:', err);
          setButtonState('error');
          setTimeout(function () {
            setButtonState('idle');
            isAdding = false;
          }, 2500);
        });
    }

    /** @param {'loading' | 'success' | 'error' | 'idle'} state */
    function setButtonState(state) {
      /** @type {HTMLButtonElement[]} */
      var buttons = [addBtn, mobileBtn].filter(
        /** @returns {b is HTMLButtonElement} @param {HTMLButtonElement | null} b */
        function (b) { return b !== null; }
      );
      buttons.forEach(
        /** @param {HTMLButtonElement} btn */
        function (btn) {
          btn.classList.remove('is-loading', 'is-success');
          switch (state) {
            case 'loading':
              btn.disabled = true;
              btn.classList.add('is-loading');
              var span = btn.querySelector('span');
              if (span) span.textContent = 'Adding…';
              else btn.textContent = 'Adding…';
              break;
            case 'success':
              btn.disabled = true;
              btn.classList.add('is-success');
              var span2 = btn.querySelector('span');
              if (span2) span2.textContent = 'Added ✓';
              else btn.textContent = 'Added ✓';
              break;
            case 'error':
              btn.disabled = false;
              var span3 = btn.querySelector('span');
              if (span3) span3.textContent = 'Error — Retry';
              else btn.textContent = 'Error — Retry';
              break;
            case 'idle':
            default:
              btn.disabled = selected.length !== 2;
              var span4 = btn.querySelector('span');
              if (span4) span4.textContent = 'Add Both to Cart';
              else btn.textContent = 'Add to Cart';
              break;
          }
        }
      );
    }

    // ===== UTILITIES =====
    /** @param {number} n */
    function round2(n) {
      return Math.round(n * 100) / 100;
    }

    /** @param {number} n */
    function formatNum(n) {
      if (n === Math.floor(n)) return n.toLocaleString('en');
      return n.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    /** @param {string} str */
    function escHtml(str) {
      var d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }
  }
})();
